import { Controller } from "@hotwired/stimulus";

// One player for the whole site. Lives in the layout with data-turbo-permanent
// so Turbo carries the element (and the playing audio) across navigations.
// Sample and full file are the same code path, only the source differs.
// Visual state is the theme's own: is-open on the modal, is-on on the music
// and effect toggles, is-playing on the main button, ambient-<key> on the
// background, and the progress ring driven by stroke-dashoffset.
const RING_LENGTH = 289.03;

export default class extends Controller {
  static targets = [
    "modal", "title", "cover", "ambient", "time", "duration", "seek", "playButton",
    "musicToggle", "volume", "volumeBubble", "effectButton", "sampleBar", "note",
    "unlockForm", "unlockInput", "downloadLink", "voice", "music"
  ];

  // initialize, not connect: the element is data-turbo-permanent, so Turbo
  // re-inserts it on every navigation and Stimulus reconnects the same
  // controller instance. Listeners added in connect() would stack up.
  initialize() {
    this.effects = new Map();
    this.enabledEffects = new Set();
    this.musicOn = true;
    this.voiceTarget.addEventListener("timeupdate", () => this.tick());
    this.voiceTarget.addEventListener("loadedmetadata", () => this.tick());
    this.voiceTarget.addEventListener("ended", () => this.pause());
    this.voiceTarget.addEventListener("error", () => this.refreshFullUrl());
    this.voiceTarget.addEventListener("play", () => this.syncMusic());
    this.voiceTarget.addEventListener("pause", () => this.syncMusic());
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.modalTarget.classList.contains("is-open")) this.close();
    });
  }

  // Fired by meditation_card_controller as a window event; detail is the card JSON.
  async open(event) {
    const meditation = event.detail;
    this.meditation = meditation;
    this.refreshes = 0;
    this.ensureAudioContext();
    this.titleTarget.textContent = meditation.title;
    this.coverTarget.style.backgroundImage = meditation.cover_url ? `url("${meditation.cover_url}")` : "none";
    this.sampleBarTarget.hidden = Boolean(meditation.full_path);
    this.downloadLinkTarget.hidden = !meditation.full_path;
    if (meditation.full_path) this.downloadLinkTarget.href = `${meditation.full_path}?download=1`;
    // The unlock field stays folded away until "Mam juz dostep" asks for it.
    if (this.hasUnlockFormTarget) this.unlockFormTarget.hidden = true;
    if (this.hasNoteTarget) { this.noteTarget.textContent = ""; this.noteTarget.hidden = true; }

    this.musicTarget.src = meditation.music_url || "";
    this.musicTarget.currentTime = 0;
    this.musicOn = Boolean(meditation.music_url);
    this.musicToggleTarget.classList.toggle("is-on", this.musicOn);
    this.musicToggleTarget.classList.toggle("is-disabled", !meditation.music_url);
    this.musicToggleTarget.setAttribute("aria-pressed", this.musicOn ? "true" : "false");
    this.setVolume();

    this.resetEffects();
    this.timeTarget.textContent = "0:00";
    this.durationTarget.textContent = "0:00";
    this.seekTarget.setAttribute("stroke-dashoffset", RING_LENGTH);

    let src = meditation.preview_url;
    if (meditation.full_path) {
      // An owner whose file endpoint errors still gets the sample rather than
      // a player that never opens.
      const full = await this.fetchFullUrl(meditation.full_path);
      if (full) {
        src = full;
      } else {
        this.sampleBarTarget.hidden = false;
        this.downloadLinkTarget.hidden = true;
        this.showNote(this.element.dataset.fullUnavailable);
      }
    }
    if (!src) return;
    this.voiceTarget.src = src;
    this.voiceTarget.currentTime = 0;
    this.modalTarget.classList.add("is-open");
    document.body.style.overflow = "hidden";
    this.play();
    this.updateMediaSession();
  }

  close() {
    this.pause();
    this.voiceTarget.currentTime = 0;
    this.musicTarget.currentTime = 0;
    this.effects.forEach((audio) => { audio.currentTime = 0; });
    this.modalTarget.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  // "Mam juz dostep" folds the code field out under the sample bar.
  toggleUnlock(event) {
    if (event) event.preventDefault();
    if (!this.hasUnlockFormTarget) return;
    this.unlockFormTarget.hidden = !this.unlockFormTarget.hidden;
    if (!this.unlockFormTarget.hidden && this.hasUnlockInputTarget) this.unlockInputTarget.focus();
  }

  toggle() { this.voiceTarget.paused ? this.play() : this.pause(); }

  play() {
    this.voiceTarget.play().catch(() => {});
    this.playButtonTarget.classList.add("is-playing");
  }

  pause() {
    this.voiceTarget.pause();
    this.playButtonTarget.classList.remove("is-playing");
  }

  back() { this.voiceTarget.currentTime = Math.max(0, this.voiceTarget.currentTime - 5); }
  forward() { this.voiceTarget.currentTime = Math.min(this.voiceTarget.duration || 0, this.voiceTarget.currentTime + 5); }

  // The slider drives the background music only; voice and effects keep their
  // own fixed levels, as on the source page.
  setVolume() {
    const value = Number(this.volumeTarget.value);
    this.musicTarget.volume = Math.min(1, value / 100);
    if (this.hasVolumeBubbleTarget) this.volumeBubbleTarget.textContent = `${value}%`;
    this.volumeTarget.parentElement?.style.setProperty("--val", `${value}%`);
    this.volumeTarget.parentElement?.parentElement?.classList.toggle("is-muted", value === 0);
  }

  toggleMusic() {
    if (this.musicToggleTarget.classList.contains("is-disabled")) return;
    this.musicOn = !this.musicOn;
    this.musicToggleTarget.classList.toggle("is-on", this.musicOn);
    this.musicToggleTarget.setAttribute("aria-pressed", this.musicOn ? "true" : "false");
    this.syncMusic();
  }

  toggleEffect(event) {
    const button = event.currentTarget;
    const url = button.dataset.url;
    const on = !button.classList.contains("is-on");
    button.classList.toggle("is-on", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
    if (on) {
      this.enabledEffects.add(url);
      let audio = this.effects.get(url);
      if (!audio) {
        audio = new Audio();
        // crossOrigin must be set before src, or the media element source
        // cannot be routed through the AudioContext (tainted stream).
        audio.crossOrigin = "anonymous";
        audio.loop = true;
        audio.src = url;
        this.effects.set(url, audio);
        this.routeThroughMixer(audio);
      }
      if (!this.voiceTarget.paused) audio.play().catch(() => {});
    } else {
      this.enabledEffects.delete(url);
      this.effects.get(url)?.pause();
    }
    this.updateAmbient();
  }

  // ---- internals ----

  showNote(text) {
    if (!this.hasNoteTarget || !text) return;
    this.noteTarget.textContent = text;
    this.noteTarget.hidden = false;
  }

  resetEffects() {
    this.effects.forEach((audio) => { audio.pause(); audio.currentTime = 0; });
    this.enabledEffects.clear();
    this.effectButtonTargets.forEach((button) => {
      button.classList.remove("is-on");
      button.setAttribute("aria-pressed", "false");
    });
    this.updateAmbient();
  }

  // The background takes the ambient scene of the first effect switched on.
  // Only these keys have a scene in the theme CSS; anything else (and no
  // effect at all) keeps the neutral "brak" background.
  updateAmbient() {
    if (!this.hasAmbientTarget) return;
    const scenes = ["deszcz", "ogien", "fale", "las"];
    const active = this.effectButtonTargets.find(
      (button) => button.classList.contains("is-on") && scenes.includes(button.dataset.fx)
    );
    const key = active ? active.dataset.fx : "brak";
    this.ambientTarget.className = `player-bg-ambient ambient-${key}`;
  }

  tick() {
    const { currentTime, duration } = this.voiceTarget;
    this.timeTarget.textContent = this.format(currentTime);
    this.durationTarget.textContent = this.format(duration);
    if (duration) {
      this.seekTarget.setAttribute("stroke-dashoffset", RING_LENGTH * (1 - currentTime / duration));
    }
    if ("mediaSession" in navigator && duration) {
      navigator.mediaSession.setPositionState({ duration, position: currentTime, playbackRate: 1 });
    }
  }

  syncMusic() {
    const wantMusic = this.musicOn && !this.voiceTarget.paused && this.musicTarget.src;
    if (wantMusic) this.musicTarget.play().catch(() => {}); else this.musicTarget.pause();
    if (this.voiceTarget.paused) {
      this.effects.forEach((audio) => audio.pause());
    } else {
      this.effects.forEach((audio, url) => { if (this.enabledEffects.has(url)) audio.play().catch(() => {}); });
    }
  }

  async fetchFullUrl(path) {
    const response = await fetch(path, { headers: { Accept: "application/json" }, credentials: "same-origin" });
    if (!response.ok) return null;
    return (await response.json()).url;
  }

  // Presigned URLs live two hours; a stalled <audio> after that gets a new one.
  // Bounded to avoid looping forever if the source keeps failing.
  async refreshFullUrl() {
    if (!this.meditation?.full_path || this.refreshes >= 2) return;
    this.refreshes += 1;
    const at = this.voiceTarget.currentTime;
    const url = await this.fetchFullUrl(this.meditation.full_path);
    if (!url) return;
    this.voiceTarget.src = url;
    this.voiceTarget.currentTime = at;
    this.play();
  }

  // iOS Safari only starts audio inside a user gesture; "open" is one, so the
  // context is created (and resumed) here and reused for every effect.
  ensureAudioContext() {
    if (!this.audioContext && window.AudioContext) this.audioContext = new AudioContext();
    this.audioContext?.resume();
  }

  routeThroughMixer(audio) {
    if (!this.audioContext) return;
    const source = this.audioContext.createMediaElementSource(audio);
    const gain = this.audioContext.createGain();
    gain.gain.value = 0.8;
    source.connect(gain).connect(this.audioContext.destination);
  }

  updateMediaSession() {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: this.meditation.title,
      artist: "Pause",
      artwork: this.meditation.cover_url ? [{ src: this.meditation.cover_url, sizes: "512x512", type: "image/jpeg" }] : []
    });
    navigator.mediaSession.setActionHandler("play", () => this.play());
    navigator.mediaSession.setActionHandler("pause", () => this.pause());
    navigator.mediaSession.setActionHandler("seekbackward", () => this.back());
    navigator.mediaSession.setActionHandler("seekforward", () => this.forward());
  }

  format(seconds) {
    if (!seconds || !isFinite(seconds)) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }
}
